import createOpenApiClient from "openapi-fetch";
import type { paths } from "./schema";

export type { paths, components } from "./schema";

/**
 * The one package the two SPAs share, and it is not UI.
 * The base URL is always the relative `/api`: the API is path-mounted on both hosts, so one
 * bundle is promotable from development to production unchanged (ADR-0005).
 */
export function createApiClient() {
  return createOpenApiClient<paths>({
    baseUrl: "/",
    // The BFF cookie is HttpOnly, so it has to be sent explicitly on same-origin fetches.
    credentials: "same-origin",
  });
}

export type ApiClient = ReturnType<typeof createApiClient>;

type ExportPath = Extract<keyof paths, `${string}/export`>;
type QueryValue = string | number | boolean | null | undefined;

/**
 * The link a list's export button downloads (ADR-0076). A plain href rather than a fetch: the
 * browser sends the session cookie and saves the file itself.
 */
export function exportHref(
  path: ExportPath,
  params: {
    path?: Record<string, string>;
    query?: Record<string, QueryValue | readonly QueryValue[]>;
  } = {},
): string {
  const url = path.replace(/\{(\w+)\}/g, (_, name: string) =>
    encodeURIComponent(params.path?.[name] ?? ""),
  );
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params.query ?? {})) {
    for (const item of ([] as QueryValue[]).concat(value)) {
      if (item !== undefined && item !== null && item !== "") {
        search.append(key, String(item));
      }
    }
  }
  const query = search.toString();
  return query ? `${url}?${query}` : url;
}

/**
 * The RFC 7807 `type` values the API answers with. A refusal the SPA must do more with than print
 * carries one of these, so it never has to branch on a Dutch sentence it does not own (ADR-0068).
 */
export const PROBLEM_TYPES = {
  inschrijvingGesloten: "urn:jbnl:inschrijving-gesloten",
  ploegnaamInGebruik: "urn:jbnl:ploegnaam-in-gebruik",
  wedstrijddagMogelijkDuplicaat: "urn:jbnl:wedstrijddag-mogelijk-duplicaat",
} as const;

/** An RFC 7807 refusal, keeping the type so a caller can branch on which rule turned it away. */
export class ProblemError extends Error {
  constructor(
    message: string,
    readonly problemType?: string,
  ) {
    super(message);
    this.name = "ProblemError";
  }
}

/** Whether a caught error is the RFC 7807 refusal named by `type`. */
export function isProblem(error: unknown, type: string): boolean {
  return error instanceof ProblemError && error.problemType === type;
}
